import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Grid, Link, Skeleton, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
} from "contexts/OutreachContacts";
import { SubscriptionContext } from "contexts/Subscription";
import { TargetedCheckInsContext } from "contexts/TargetedCheckIns";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActionItem, UpgradeDialogSource } from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import SendButtonGroup from "components/EmailThread/EmailDraft/SendButtonGroup";
import { getTomorrow } from "components/EmailThread/EmailDraft/utils";
import { getPageName } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { makeDeepCopy } from "utils/updateLocalState";

import HalfScreenPreviewCard from "../MainEmailThread/HalfScreenPreviewCard";
import EnableImport from "./EnableImport";
import OrderBy from "./OrderBy";
import PreviewCard from "./PreviewCard";

const REF_OFFSET = 9;

interface Props {
  totalPages: number;
  isSmallScreen: boolean;
}

const CheckinsSidebar = ({ totalPages, isSmallScreen }: Props) => {
  const navigate = useNavigate();

  // Contexts
  const {
    total,
    actionItems,
    setActionItems,
    fetchTargetedActionItems,
    page,
    setActionItem,
    fetchSingleActionItem,
    fetchMoreLoading,
    actionItem,
    orderBy,
  } = useContext(TargetedCheckInsContext);
  const [searchParams] = useSearchParams();
  const { setTotals } = useContext(OutreachContactsContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const { userIntegration } = useContext(UserIntegrationsContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const isFreeUser =
    (!subscription || !subscription?.isSubscribed) &&
    !currentOrg?.overridePlanName;

  const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));
  const successCheckout = searchParams.get("checkout") === "success";

  const [openUploadGmail, setOpenUploadGmail] = useState(false);
  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  // States
  const [sendLoading, setSendLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [generateLoading, setGenerateLoading] = useState(false);

  const [openSendAllConfirmDialog, setOpenSendAllConfirmDialog] =
    useState(false);
  const [scheduleAt, setScheduleAt] = useState<string | null>(null);

  const handleConfirm = () => {
    setOpenSendAllConfirmDialog(false);
    sendEmails(undefined, true, scheduleAt);
    setScheduleAt(null);
  };

  const handleSelectAll = () => {
    setSelectAll((prev) => {
      if (!prev) {
        setSelectedIds(actionItems?.map((x) => x.id));
      } else {
        setSelectedIds([]);
      }
      return !prev;
    });
  };

  const handleGenerateForUser = async () => {
    try {
      setGenerateLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/targeted-check-ins/generate-for-user`,
        "POST",
        {},
      );
      setAlert("Request sent", "success");
      setCurrentOrg((prev) => {
        if (prev) {
          return {
            ...prev,
            lastGeneratedCheckInsAt: Math.floor(Date.now() / 1000),
          };
        }
        return prev;
      });
    } catch (error) {
      if (error?.message?.includes("Google")) {
        setOpenUploadGmail(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setGenerateLoading(false);
    }
  };

  const sendEmails = async (
    actionItemIds?: number[],
    sendAll?: boolean,
    scheduleAt?: string | null,
  ) => {
    setSendLoading(true);
    try {
      const { failedBrands } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/send-multi`,
        "POST",
        {},
        {
          actionItemIds,
          sendAll,
          scheduleAt,
          sendFrom: getPageName(),
        },
      );
      if (failedBrands?.length > 0) {
        setAlert(
          `Cannot sent email to these brands: ${failedBrands?.join(", ")}`,
          "warning",
        );
      } else {
        setAlert(
          actionItemIds && actionItemIds?.length > 0
            ? `${actionItemIds?.length} emails are scheduled to be sent`
            : "Sent all your emails",
          "success",
        );
      }
      if (sendAll) {
        setActionItem(null);
        setActionItems([]);
      } else {
        setActionItem(null);
        setActionItems((prev) =>
          prev?.filter((item) => !actionItemIds?.includes(item.id)),
        );
        setTotals((prev) => ({
          ...prev,
          [InboxSidebarOptionKey.checkins]: Math.max(
            (prev[InboxSidebarOptionKey.checkins] || 0) -
              (actionItemIds ? actionItemIds?.length : 0),
            0,
          ),
        }));
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSendLoading(false);
    }
  };

  const selectItem = (item: ActionItem) => {
    const index = selectedIds?.findIndex((id) => id === item.id);
    if (index === -1) {
      setSelectedIds((prev) => {
        const copy = makeDeepCopy(prev);
        copy.push(item.id);
        return copy;
      });
    } else {
      setSelectedIds((prev) => {
        const copy = makeDeepCopy(prev);
        copy.splice(index, 1);
        if (copy.length === 0) {
          setSelectAll(false);
        }
        return copy;
      });
    }
  };

  const clickOnActionItem = async (actionItem: ActionItem) => {
    setActionItem(actionItem);
    await fetchSingleActionItem(actionItem.id);
    if (!isSmallScreen) {
      navigate(
        `/${routes.inboxCheckins}/${actionItem.id}/contact/${actionItem.outreachContactId}/email/${actionItem.gmailThreadId}`,
      );
    }
  };

  const renderButtonGroups = (firstButtonTheme = {}, firstArrowTheme = {}) => {
    return (
      <SendButtonGroup
        handleSendDraftEmail={(scheduleAt?: string | null) => {
          sendEmails(selectedIds, false, scheduleAt);
        }}
        sendLoading={sendLoading}
        defaultTime={getTomorrow()}
        buttonText="Selected Drafts"
        disabled={actionItems?.length === 0}
        buttonTheme={firstArrowTheme}
        arrowTheme={firstButtonTheme}
      />
    );
  };

  const renderMessage = () => {
    if (
      !currentOrg?.allowImportEmail &&
      !userIntegration?.partnershipsSearchedAt
    ) {
      return (
        <Grid container alignItems="center" direction="column">
          <Typography textAlign="center" sx={{ color: grey[500], px: 3 }}>
            Allow Bento to track past collaborations and remind you when it's
            time to follow up.
          </Typography>

          <Button
            size="small"
            variant="contained"
            disableElevation
            sx={{ mt: 2 }}
            onClick={() => setOpenUploadGmail(true)}
          >
            Grant Permission
          </Button>
        </Grid>
      );
    } else if (!currentOrg?.lastGeneratedCheckInsAt) {
      return (
        <Grid container alignItems="center" direction="column">
          <Typography textAlign="center" sx={{ color: grey[500], px: 3 }}>
            Generate check-ins — Bento will scan your past collaborations and
            create follow-up drafts for you.
          </Typography>

          <LoadingButton
            size="small"
            variant="contained"
            disableElevation
            sx={{ mt: 2 }}
            onClick={handleGenerateForUser}
            loading={generateLoading}
          >
            Generate
          </LoadingButton>
        </Grid>
      );
    } else if (!currentOrg?.lastCompletedCheckInsAt) {
      return (
        <Typography textAlign="center" sx={{ color: grey[500], p: 3 }}>
          We are currently generating your check-in drafts. This may take a few
          hours. We will send an email notification once it's complete! ⏳
        </Typography>
      );
    } else {
      return (
        <Typography textAlign="center" sx={{ color: grey[500], p: 3 }}>
          You don't have any drafts for past partnership check-ins at this time.
        </Typography>
      );
    }
  };

  const handleSubscribe = () => {
    setUpgradeDialogSource(UpgradeDialogSource.targeted_check_ins);
  };

  useEffect(() => {
    if (isPaginationRefVisible && page < totalPages) {
      fetchTargetedActionItems(page + 1, orderBy, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible]);

  useEffect(() => {
    if (successCheckout) {
      setActionItem(null);
      fetchTargetedActionItems(1);
      navigate(`/${routes.inboxCheckins}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCheckout]);

  return (
    <Box sx={{ position: "relative", height: "100%", overflowY: "auto" }}>
      <Box
        sx={{
          p: isSmallScreen ? 1 : 3,
          borderBottom: `1px solid ${grey[200]}`,
        }}
      >
        {!isSmallScreen && (
          <Typography variant="h6" gutterBottom>
            Drafted Emails{" "}
            <Link
              href="https://www.loom.com/share/18d18ef271c847458d0a9a999fe2c6f7?sid=d375d598-5215-4457-9dfa-be9ae6036362"
              target="_blank"
            >
              (View Tutorial{" "}
              <Box
                component="i"
                className="fa-regular fa-arrow-up-right-from-square"
              />
              )
            </Link>
          </Typography>
        )}

        {actionItems?.length === 0 ? (
          <>{renderMessage()}</>
        ) : (
          <>
            {!isSmallScreen && (
              <Grid container gap={2} sx={{ mb: 2 }} alignItems="center">
                {renderButtonGroups()}
              </Grid>
            )}

            <Grid
              container
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Link
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  textUnderlineOffset: "3px",
                  color: "primary.main",
                }}
                onClick={handleSelectAll}
              >
                {selectAll ? "Unselect All" : "Select All"}
                {selectedIds?.length > 0 ? ` (${selectedIds?.length})` : ""}
              </Link>

              {isAdmin && <OrderBy />}
            </Grid>
          </>
        )}
      </Box>

      {actionItems?.length > 0 && (
        <Box
          sx={{
            height: {
              xs: "calc(100vh - 250px)",
              lg: "calc(100vh - 250px)",
            },
            position: "relative",
            overflowY: "scroll",
          }}
        >
          {actionItems?.map((item, index) => (
            <Grid
              key={item.id}
              item
              ref={
                (
                  REF_OFFSET > actionItems?.length
                    ? actionItems.length - 1 === index
                    : actionItems.length - REF_OFFSET === index
                )
                  ? paginationRef
                  : null
              }
            >
              <PreviewCard
                actionItem={item}
                selectedIds={selectedIds}
                selectItem={selectItem}
                clickOnActionItem={clickOnActionItem}
              />
            </Grid>
          ))}

          {fetchMoreLoading && (
            <Grid container>
              <Skeleton
                animation="wave"
                width="100%"
                height="250px"
                sx={{ mt: -3 }}
              />
              <Skeleton
                animation="wave"
                width="100%"
                height="250px"
                sx={{ mt: -10 }}
              />
            </Grid>
          )}
          {isFreeUser &&
            currentOrg?.lastGeneratedCheckInsAt &&
            total > actionItems?.length && (
              <>
                <Typography sx={{ px: 3, mt: 1 }}>
                  👀{" "}
                  <em>
                    You are currently on the Free plan. Upgrade to unlock all of
                    your drafted emails.
                  </em>
                </Typography>

                <Grid container justifyContent="center" sx={{ my: 2 }}>
                  <Button variant="outlined" onClick={handleSubscribe}>
                    Upgrade
                  </Button>
                </Grid>
              </>
            )}
        </Box>
      )}

      {isSmallScreen && actionItem?.id && <HalfScreenPreviewCard />}

      {isSmallScreen && !actionItem?.id && actionItems?.length > 0 && (
        <Grid
          container
          sx={{
            py: 2,
            backgroundColor: "primary.main",
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
          }}
          justifyContent={"center"}
          gap={2}
        >
          {renderButtonGroups(
            {
              backgroundColor: "white",
              color: "primary.main",
            },
            {
              backgroundColor: "white",
              color: "primary.main",
            },
          )}
        </Grid>
      )}

      <ConfirmDialog
        header={`Send all the drafted emails?`}
        subheader={
          <>
            This will send <strong>all {total} drafted emails</strong>. To send
            all, please click the Confirm button below.
          </>
        }
        open={openSendAllConfirmDialog}
        handleClose={() => setOpenSendAllConfirmDialog(false)}
        handleConfirm={handleConfirm}
      />
      <EnableImport
        open={openUploadGmail}
        handleClose={() => setOpenUploadGmail(false)}
      />
    </Box>
  );
};

export default CheckinsSidebar;
